import type { BrandKind, PartnerBrand } from "../../lib/partner-brand.js";

/**
 * Partner wordmark renderer. Branches on the brand `kind` discriminator
 * to apply the right typography + per-brand emphasis (e.g. Transition33's
 * gold-on-ink split between "Transition" and "33").
 *
 * Sizes are class-based (sm/md/lg/xl) so the same component fits
 * the magic-link email landing page (xl), the sign-in card header (lg),
 * and the authenticated portal header chrome (md).
 *
 * Canon: docs/design-canon/gop/design_handoff_transition33_welcome
 * (Transition33 wordmark spec — Inter 700, no serif, no gap between
 * "Transition" and "33", "33" in gold-500 / ink-900 split).
 */

type WordmarkSize = "sm" | "md" | "lg" | "xl";

interface PartnerWordmarkProps {
  brand: PartnerBrand;
  size?: WordmarkSize;
  className?: string;
}

export function PartnerWordmark({ brand, size = "md", className = "" }: PartnerWordmarkProps) {
  return renderForKind(brand.kind, size, className);
}

function renderForKind(kind: BrandKind, size: WordmarkSize, className: string) {
  switch (kind) {
    case "transition33":
      return <Transition33Wordmark size={size} className={className} />;
    case "latitude30":
    default:
      return <Latitude30Wordmark size={size} className={className} />;
  }
}

// ─── Transition33 ──────────────────────────────────────────
//
// "Transition" in ink, "33" in gold, Inter 700, no gap, baseline
// shared. Per the handoff: never use a serif on the "33".

const TRANSITION33_SIZE_CLASSES: Record<WordmarkSize, string> = {
  // text-[NN]px → exact sizes per the handoff for the various surfaces
  sm: "text-2xl tracking-tight",
  md: "text-3xl tracking-tight",
  lg: "text-5xl tracking-tight",
  xl: "text-[84px] leading-[0.92] tracking-[-0.04em]",
};

function Transition33Wordmark({ size, className }: { size: WordmarkSize; className: string }) {
  const sizeClasses = TRANSITION33_SIZE_CLASSES[size];
  return (
    <h1
      className={`m-0 whitespace-nowrap font-bold ${sizeClasses} ${className}`}
      style={{ fontFamily: "var(--font-display)" }}
      aria-label="Transition33"
    >
      <span style={{ color: "var(--color-fg)" }}>Transition</span>
      <span style={{ color: "var(--color-accent)" }}>33</span>
    </h1>
  );
}

// ─── LATITUDE30 ────────────────────────────────────────────
//
// All-caps, letter-spaced, single colour (brand teal). Used for any
// partner without their own brand override.

const LATITUDE30_SIZE_CLASSES: Record<WordmarkSize, string> = {
  sm: "text-sm",
  md: "text-lg",
  lg: "text-2xl",
  xl: "text-4xl",
};

function Latitude30Wordmark({ size, className }: { size: WordmarkSize; className: string }) {
  const sizeClasses = LATITUDE30_SIZE_CLASSES[size];
  return (
    <span
      className={`font-bold tracking-[0.18em] text-brand ${sizeClasses} ${className}`}
      aria-label="LATITUDE30"
    >
      LATITUDE30
    </span>
  );
}
